import { useState } from 'react';
import * as servicesApi from '../api/memberServices';
import { useToast } from './toast';
import { EmptyState, TextareaField, formatDateTime } from './ui';
import { cn, ui } from '../lib/ui';
import { formatMoney } from '../lib/payments';
import { ACTIONS, ACTION_LABELS, STATUS_LABELS, STATUS_TONE } from '../lib/memberServices';
import { fieldErrorsFrom } from '../lib/formErrors';

const TONE = {
  success: ui.pillSuccess,
  warning: ui.pillWarning,
  info: ui.pillInfo,
  danger: ui.pillDanger,
  neutral: ui.pillNeutral,
};

function RequestCard({ request, showOrg, showRequester, onChanged }) {
  const toast = useToast();
  const [pending, setPending] = useState(null);
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState(null);
  const [busy, setBusy] = useState(false);

  async function run(action) {
    if (ACTIONS[action].noteRequired && pending !== action) {
      setPending(action);
      return;
    }
    if (ACTIONS[action].noteRequired && note.trim().length < 5) {
      setNoteError('Say why, so the member knows what to do next');
      return;
    }
    setBusy(true);
    try {
      const data = await servicesApi.actOnServiceRequest(request.requestId, action, note.trim());
      toast.success(`${request.serviceName}: ${STATUS_LABELS[data.request.status].toLowerCase()}`);
      setPending(null);
      setNote('');
      onChanged?.(data.request);
    } catch (err) {
      setNoteError(fieldErrorsFrom(err).note || null);
      toast.error(err.message || 'That did not work');
    } finally {
      setBusy(false);
    }
  }

  const actions = request.actions || [];
  return (
    <article className={cn(ui.card, 'mb-3')} aria-label={`${request.serviceName} request`}>
      <div className={cn(ui.row, ui.spread, 'mb-2 items-start')}>
        <div>
          <h3 className="m-0 text-title-s">{request.serviceName}</h3>
          <p className={cn(ui.hint, 'm-0')}>
            {showOrg && `${request.orgName} · `}
            {showRequester && `${request.requesterName || 'A member'} · `}
            {formatDateTime(request.createdAt)} · {formatMoney(request.feeCents)} to the
            organization
          </p>
        </div>
        <span className={cn(TONE[STATUS_TONE[request.status]], 'shrink-0')}>
          {STATUS_LABELS[request.status]}
        </span>
      </div>
      <p className="mt-0 mb-2 whitespace-pre-wrap text-body-s">{request.details}</p>
      {request.lastNote && (
        <p className={cn(ui.muted, 'mt-0 mb-2 text-body-s')}>
          <strong>Note:</strong> {request.lastNote}
        </p>
      )}
      {pending && (
        <TextareaField
          label="Why are you declining it?"
          value={note}
          onChange={e => {
            setNote(e.target.value);
            setNoteError(null);
          }}
          rows={2}
          maxLength={1000}
          error={noteError}
        />
      )}
      {actions.length > 0 && (
        <div className={ui.row}>
          {actions.map(action => (
            <button
              key={action}
              type="button"
              disabled={busy}
              className={cn(
                action === 'decline' || action === 'cancel' ? ui.btnSecondary : ui.btnPrimary,
                ui.btnSm
              )}
              onClick={() => run(action)}
            >
              {pending === action ? `Confirm: ${ACTION_LABELS[action]}` : ACTION_LABELS[action]}
            </button>
          ))}
          {pending && (
            <button
              type="button"
              className={cn(ui.btnGhost, ui.btnSm)}
              onClick={() => {
                setPending(null);
                setNoteError(null);
              }}
            >
              Back
            </button>
          )}
        </div>
      )}
    </article>
  );
}

/**
 * Member service requests (docs/Decisions.md D66) with the actions the API
 * says the viewer may take on each. Used by the Services page (the member's
 * own and the org admin's) and Platform admin -> Member services.
 */
export default function ServiceRequestList({
  requests,
  empty,
  showOrg = false,
  showRequester = false,
  onChanged,
}) {
  if (!requests.length) return <EmptyState message={empty} />;
  return requests.map(request => (
    <RequestCard
      key={request.requestId}
      request={request}
      showOrg={showOrg}
      showRequester={showRequester}
      onChanged={onChanged}
    />
  ));
}
